import { FC, useCallback, useEffect, useMemo } from 'react';
import { Box, Container } from '@bridebook/ui';
import GuestNameForm from './guest-name-form/guest-name-form';
import {
  ConfirmationStep,
  DeadlinePassedStep,
  EventsStep,
  NoteStep,
  QuestionsStep,
  SummaryStep,
} from './guest-rsvp-form/substeps';
import { useRsvpNavigation, useRsvpState, useRsvpData } from './guest-rsvp.context';
import componentStyles from './guest-rsvp-main-component.style';
import { getThemeProperties } from '@bridebook/web/lib/wedding-website/utils';
import GuestRsvpTopbar from './partials/topbar';
import PageHeaderDesktop from './partials/page-header-desktop';
import { useTriggerTopScroll } from '@bridebook/web/app-shared/lib/ui/hooks/use-trigger-top-scroll';

// This is a component that is shared between live and preview modes.

const RSVP_STEP_COMPONENTS = {
  'RSVP_FORM.EVENTS': EventsStep,
  'RSVP_FORM.QUESTIONS': QuestionsStep,
  'RSVP_FORM.NOTE': NoteStep,
  'RSVP_FORM.SUMMARY': SummaryStep,
  'RSVP_FORM.CONFIRMATION': ConfirmationStep,
  'RSVP_FORM.DEADLINE_PASSED': DeadlinePassedStep,
} as const;

const GuestRsvpMainComponent: FC = () => {
  const { step, setStep, currentQuestionIndex } = useRsvpNavigation();
  const { isRsvpOpen, rsvpSubmissionStatus, isPreview } = useRsvpState();
  const { theme } = useRsvpData();
  const triggerTopScroll = useTriggerTopScroll();

  const themeProperties = useMemo(() => getThemeProperties(theme), [theme]);
  const styles = useMemo(
    () => componentStyles(themeProperties, isPreview),
    [themeProperties, isPreview],
  );

  const topId = isPreview ? 'rsvp-preview-top' : 'top';

  const scrollToTop = useCallback(() => {
    if (isPreview) {
      document.getElementById(topId)?.scrollIntoView({ block: 'start' });
    } else {
      triggerTopScroll();
    }
  }, [isPreview, topId, triggerTopScroll]);

  useEffect(() => {
    if (step === 'NAME_FORM') return;

    if (
      !isPreview &&
      rsvpSubmissionStatus === 'FIRST_SUBMISSION' &&
      !isRsvpOpen &&
      step !== 'RSVP_FORM.DEADLINE_PASSED'
    ) {
      setStep('RSVP_FORM.DEADLINE_PASSED');
    }
  }, [isPreview, rsvpSubmissionStatus, isRsvpOpen, step, setStep]);

  useEffect(() => {
    scrollToTop();
  }, [step, currentQuestionIndex, scrollToTop]);

  const renderContent = () => {
    if (step === 'NAME_FORM') {
      return <GuestNameForm />;
    }

    const StepComponent = RSVP_STEP_COMPONENTS[step] || EventsStep;
    return <StepComponent />;
  };

  return (
    <>
      {!isPreview && <PageHeaderDesktop />}
      <Box style={styles.wrapper}>
        <Box id={topId} />
        <Container noPadding fullWidth={isPreview} style={styles.innerWrapper}>
          <GuestRsvpTopbar />
          <Box style={styles.contentWrapper}>{renderContent()}</Box>
        </Container>
      </Box>
    </>
  );
};

export default GuestRsvpMainComponent;
